import React, { useState } from "react";
import Collapse from "../../../components/Collapse";
import { formatBook as fmtBook } from "../../../utils/formatOdds";

/**
 * Teenpatti - 2.0 (teen6) bet area — reference markup (.casino-table.teenpatti2 > .casino-detail):
 *   .teen1daycasino-container (Player A / Player B Main back+lay, .teen2uo Under 21 / Over 22 per player)
 *   .tee2suit (suit odds header + spade/heart/club/diamond cells), .row.row5 (.teen2eo Odd/Even, .teen2cards A..K)
 * Styled by the ported reference CSS (index.css `.teenpatti2 ...`). Suspended = gstatus !== "OPEN";
 * book is green when > 0, otherwise red (reference v-casinobook / v-casinofancybook). A suspended suit or
 * odd/even cell with a non-zero book gets .lock-top (tenexch: padlock at the top only with a liability).
 * Side bets are stored per card ("Card 6 Spade", "Card 6 Odd", "Card 6 K"), so their book is read by that name.
 * Clicks call onBetClick(value, selection, type, betData) — the page keeps the bet/exposure logic.
 */

const IMG = `${import.meta.env.BASE_URL}img/cards`;
const SUIT_IMGS = ["spade", "heart", "club", "diamond"];
const CARD_IMGS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];

// Reference v-kvalmin / v-kvalmax: 1000 → 1K, 100000 → 1L
const kval = (n) => {
  const v = Number(Number(n || 0).toFixed(2));
  if (v >= 1e3 && v < 1e5) return `${parseFloat((v / 1e3).toFixed(2))}K`;
  if (v >= 1e5) return `${parseFloat((v / 1e5).toFixed(2))}L`;
  return parseFloat(v);
};

const bookClass = (v) => (Number(v) > 0 ? "book-green" : "book-red");
const bySr = (a, b) => (a?.sr ?? 0) - (b?.sr ?? 0);
const bySno = (a, b) => (a?.sno ?? 0) - (b?.sno ?? 0);

export default function BetTableTeenPatti6({ data = [], onBetClick, exposures = {} }) {
  const [openRanges, setOpenRanges] = useState({});
  const toggleRange = (id) => setOpenRanges((prev) => ({ ...prev, [id]: !prev[id] }));

  const getExposure = (name) => {
    if (!name) return 0;
    const keys = [name, name.toLowerCase(), name.trim(), name.toLowerCase().trim()];
    for (const key of keys) {
      if (key && exposures[key] !== undefined && exposures[key] !== null) {
        const v = parseFloat(exposures[key]);
        if (!isNaN(v)) return v;
      }
    }
    return 0;
  };

  const sub = Array.isArray(data) ? data : [];
  const main = sub.filter((i) => i.subtype === "teen6").sort(bySr);
  const underover = sub.filter((i) => i.subtype === "uo").sort(bySr);
  const suit = sub.filter((i) => i.subtype === "suit" && Number(i.visible) === 1).sort(bySr)[0];
  const oddEven = sub.filter((i) => i.subtype === "oddeven" && Number(i.visible) === 1).sort(bySr)[0];
  const cardsData = sub.filter((i) => i.subtype === "cards" && Number(i.visible) === 1).sort(bySr)[0];

  const click = (value, selection, type, betData) => {
    if (!value || !onBetClick) return;
    onBetClick(value, selection, type, betData);
  };

  const rangeIcon = (id, item) => (
    <>
      <i
        className={openRanges[id] ? "fas fa-info-circle" : "fas fa-info-circle collapsed"}
        data-toggle="collapse"
        data-target={`#${id}`}
        aria-expanded={openRanges[id] ? "true" : "false"}
        onClick={() => toggleRange(id)}
      />{" "}
      <Collapse id={id} open={!!openRanges[id]} className="icon-range">
        R:<span>{kval(item?.min)}</span>-<span>{kval(item?.max)}</span>
      </Collapse>
    </>
  );

  const titleRow = (player, withBackLay, extraClass) => (
    <div className={`casino-box-row ${extraClass}`}>
      <div className="casino-nation-name no-border casino-bl-box-title">
        <div className={player === "A" ? "playera" : "playerb"}>Player {player}</div>
      </div>{" "}
      <div className="casino-bl-box casino-bl-box-title">
        {withBackLay ? (
          <>
            <div className="casino-bl-box-item" {...(player === "B" ? { "data-toggle": "modal" } : {})}>
              <b>Back</b>
            </div>{" "}
            <div className="casino-bl-box-item">
              <b>Lay</b>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );

  const mainRow = (item, player, rangeId) => {
    const suspended = item?.gstatus !== "OPEN";
    const nat = item?.nat || `Player ${player}`;
    const book = getExposure(nat);
    return (
      <div className="casino-box-row">
        <div className="casino-nation-name">
          <b className="d-none-small">Main</b> <b className="d-none-big">Player {player}</b>{" "}
          <div className="float-right">
            <span className={`mr-2 casino-book ${bookClass(book)}`}>{fmtBook(book)}</span> {rangeIcon(rangeId, item)}
          </div>
        </div>{" "}
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${suspended ? " suspended" : ""}`}
            onClick={() => !suspended && click(item?.b, `${nat} Back`, "back", item)}
          >
            <span className="casino-box-odd">{item?.b ?? 0}</span>
          </div>{" "}
          <div
            className={`lay casino-bl-box-item${suspended ? " suspended" : ""}`}
            onClick={() => !suspended && click(item?.l, `${nat} Lay`, "lay", item)}
          >
            <span className="casino-box-odd">{item?.l ?? 0}</span>
          </div>
        </div>
      </div>
    );
  };

  const uoCell = (item, label, rangeId) => {
    const suspended = item?.gstatus !== "OPEN";
    const book = getExposure(item?.nat || label);
    return (
      <>
        <div className="casino-nation-name">
          <b>{label}</b>{" "}
          <div className="float-right">
            <span className={`mr-2 casino-book ${bookClass(book)}`}>{fmtBook(book)}</span> {rangeIcon(rangeId, item)}
          </div>
        </div>{" "}
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${suspended ? " suspended" : ""}`}
            onClick={() => !suspended && click(item?.b, item?.nat || label, "back", item)}
          >
            <span className="casino-box-odd">{item?.b ?? 0}</span>
          </div>
        </div>
      </>
    );
  };

  const uoSide = (player, items, ranges) => (
    <>
      {titleRow(player, false, "d-none-big")}{" "}
      <div className="casino-box-row">
        {uoCell(items[0], "Under 21", ranges[0])} {uoCell(items[1], "Over 22", ranges[1])}
      </div>
    </>
  );

  // The feed lists the suits Spade, Diamond, Club, Heart — not the image order — so each
  // cell takes its entry by name (by position the heart image showed the Diamond odds).
  const suitOdds = SUIT_IMGS.map((s) => (suit?.odds || []).find((o) => String(o.nat).toLowerCase() === s));
  const suitSuspended = suit?.gstatus !== "OPEN";
  const oeOdds = [...(oddEven?.odds || [])].sort(bySno);
  const oeSuspended = oddEven?.gstatus !== "OPEN";
  const cardOdds = cardsData?.odds || [];
  const cardsSuspended = cardsData?.gstatus !== "OPEN";

  return (
    <>
      <div className="teen1daycasino-container">
        <div className="teen1dayleft">
          {titleRow("A", true, "d-none-small")} {mainRow(main[0], "A", "range1")}
        </div>{" "}
        <div className="teen1daycenter" />{" "}
        <div className="teen1dayright">
          {titleRow("B", true, "d-none-small")} {mainRow(main[1], "B", "range2")}
        </div>{" "}
        <div className="teen2uo">
          <div className="teen1dayleft">{uoSide("A", [underover[0], underover[1]], ["range3", "range4"])}</div>{" "}
          <div className="teen1daycenter" />{" "}
          <div className="teen1dayright">{uoSide("B", [underover[2], underover[3]], ["range5", "range6"])}</div>
        </div>
      </div>{" "}
      {suit ? (
        <div className="tee2suit mt-1">
          <div className="casino-bl-box tee2suitheader">
            {suitOdds.map((o, i) => (
              <div key={i} className="casino-bl-box-item casino-card-img">
                <span className="casino-box-odd">{o?.b ?? 0}</span>
              </div>
            ))}
          </div>{" "}
          <div className="casino-bl-box">
            {suitOdds.map((o, i) => {
              const book = getExposure(o && `${suit.nat} ${o.nat}`);
              return (
                <div
                  key={`suit${i}`}
                  className={`casino-bl-box-item back casino-card-img${suitSuspended ? ` suspended${book ? " lock-top" : ""}` : ""}`}
                  onClick={() => !suitSuspended && o && click(o.b, o.nat, "back", { ...suit, nat: o.nat, odds: o })}
                >
                  <img src={`${IMG}/${SUIT_IMGS[i]}.png`} alt="" />{" "}
                  <span className={`casino-book ${bookClass(book)}`}>{fmtBook(book)}</span>
                </div>
              );
            })}
          </div>{" "}
          <div className="casino-min-max text-right mt-4">
            R:<span>{kval(suit.min)}</span>-<span>{kval(suit.max)}</span>
          </div>
        </div>
      ) : null}{" "}
      <div className="row row5 mt-3">
        <div className="col-12 col-lg-4">
          {oddEven ? (
            <>
              <div className="teen2eo">
                <div className="casino-bl-box tee2eoheader">
                  {oeOdds.map((o, i) => (
                    <div key={`oddevenods${i}`} className="casino-bl-box-item casino-card-img">
                      <span className="casino-box-odd">{o.b}</span>
                    </div>
                  ))}
                </div>{" "}
                <div className="casino-bl-box">
                  {oeOdds.map((o, i) => {
                    const book = getExposure(`${oddEven.nat} ${o.nat}`);
                    return (
                      <div
                        key={`oddeven${i}`}
                        className={`casino-bl-box-item back${oeSuspended ? ` suspended${book ? " lock-top" : ""}` : ""}`}
                        onClick={() => !oeSuspended && click(o.b, o.nat, "back", { ...oddEven, nat: o.nat, odds: o })}
                      >
                        <span className="casino-box-odd">{o.nat}</span>{" "}
                        <span className={`casino-book ${bookClass(book)}`}>{fmtBook(book)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>{" "}
              <div className="casino-min-max text-right mt-3">
                R:<span>{kval(oddEven.min)}</span>-<span>{kval(oddEven.max)}</span>
              </div>
            </>
          ) : null}
        </div>{" "}
        <div className="col-12 col-lg-8">
          {cardsData ? (
            <div className="teen2cards">
              <div className="casino-cards text-center mt-1">
                {cardOdds.map((o, i) => {
                  const book = getExposure(`${cardsData.nat} ${String(o.nat).replace(/^Card\s+/i, "")}`);
                  return (
                    <div key={`cards${i}`} className="casino-card-item">
                      <div className="casino-box-odd">{o.b}</div>{" "}
                      <div
                        className={`card-image${cardsSuspended ? " suspended" : ""}`}
                        onClick={() => !cardsSuspended && click(o.b, o.nat, "back", { ...cardsData, nat: o.nat, odds: o })}
                      >
                        {CARD_IMGS[i] ? <img src={`${IMG}/${CARD_IMGS[i]}.png`} alt="" /> : null}
                      </div>{" "}
                      <div className={`casino-book ${bookClass(book)}`}>{fmtBook(book)}</div>
                    </div>
                  );
                })}
              </div>{" "}
              <div className="casino-min-max text-right">
                R:<span>{kval(cardsData.min)}</span>-<span>{kval(cardsData.max)}</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
